import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CourseObjective {
  id: string;
  description: string;
  bloom_level?: string | null;
}

export interface CourseExerciseRef {
  title: string;
  source: string;
  difficulty?: string | null;
}

/** One two-level subsection of a chapter — a teaching unit of the guided mode
 *  (from the course PDF's outline, with its page span). */
export interface CourseChapterSection {
  number?: string | null;
  title: string;
  page_start?: number | null;
  page_end?: number | null;
}

/** Compiled lesson script of one teaching unit (chapter_unit_scripts). */
export interface CourseUnitScript {
  unit_number: number;
  lang: string;
  status: 'compiling' | 'done' | 'failed' | string;
}

export interface CourseStructureChapter {
  id: string;
  display_order: number;
  title: string;
  section_number?: number | null;
  summary?: string | null;
  moodle_cmids: number[];
  objectives: CourseObjective[];
  sections?: CourseChapterSection[];
  page_start?: number | null;
  page_end?: number | null;
  is_assessment: boolean;
  exercises: CourseExerciseRef[];
  unit_scripts?: CourseUnitScript[];
}

/** State of the unit-script compilation job for a course (sync_jobs). Lets the
 *  dialog tell "not started" from "still running" from "failed" — the script
 *  count alone showed 0/N for all three. */
export interface CourseScriptJob {
  status: string;               // queued | running | done | failed | dead
  attempts: number;
  max_attempts: number;
  stale: boolean;               // worker died mid-run; queue reclaims it
  last_error?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface CourseStructureResponse {
  course_id: string | null;
  structure: { id: string; version: number; status: string; language?: string | null } | null;
  script_job?: CourseScriptJob | null;
  chapters: CourseStructureChapter[];
  path: any;
}

/** Lernziel-Register (PRD mastery-objectives M2/M3): one objective with its origin. */
export interface RegisterObjective {
  objective_key: string;
  source: 'material' | 'lecturer' | 'client';
  external_ref: string | null;
  description: string;
  bloom_level: string | null;
  dqr_area: string | null;
  dqr_source: string | null;
  status: 'active' | 'retired';
  chapter_id: string | null;
  chapter_order: number | null;
  chapter_title: string | null;
}

export interface RegisterLink {
  source_key: string;
  status: 'proposed' | 'confirmed' | 'rejected';
  origin: 'suggested' | 'lecturer' | 'import';
  score: number | null;
}

export interface ObjectiveRegister {
  objectives: RegisterObjective[];
  links: Record<string, RegisterLink[]>;
}

@Injectable({ providedIn: 'root' })
export class CourseStructureService {
  private baseUrl = `${environment.tutorsBackend}`;

  constructor(private http: HttpClient) {}

  getByCourse(courseId: string) {
    return this.http
      .get(`${this.baseUrl}/course-structure/${courseId}`)
      .pipe(map((r: any) => r as CourseStructureResponse));
  }

  /** Keyed by Moodle canonical course uuid (moodle_courses_canonical.id). */
  getByCanonical(canonicalId: string) {
    return this.http
      .get(`${this.baseUrl}/course-structure/by-canonical/${canonicalId}`)
      .pipe(map((r: any) => r as CourseStructureResponse));
  }

  /** Content-difficulty report (per-objective fail-rate, hardest first),
   *  keyed by the Moodle canonical course uuid. */
  getDifficultyByCanonical(canonicalId: string) {
    return this.http
      .get(`${this.baseUrl}/course-structure/by-canonical/${canonicalId}/difficulty`)
      .pipe(map((r: any) => r as CourseDifficultyResponse));
  }

  /** Manual re-parse ("redo") — force-bypasses the content-hash skip. */
  reparseByCanonical(canonicalId: string) {
    return this.http
      .post(`${this.baseUrl}/course-structure/by-canonical/${canonicalId}/reparse`, {})
      .pipe(map((r: any) => r as { enqueued: boolean; no_content: boolean }));
  }

  /** Compiled lesson script of one teaching unit, plus its review notes. */
  /** Wie `reparseByCanonical`, nur mit unserer Kurskennung — ILIAS-Zeilen
   *  tragen sie direkt an der Platzierung und brauchen die Umschlüsselung
   *  über eine kanonische Moodle-Kennung nicht. */
  reparseByCourse(courseId: string) {
    return this.http
      .post(`${this.baseUrl}/course-structure/${courseId}/reparse`, {})
      .pipe(map((r: any) => r as { enqueued: boolean; no_content: boolean }));
  }

  getParseStatusByCourse(courseId: string) {
    return this.http
      .get(`${this.baseUrl}/course-structure/${courseId}/parse-status`)
      .pipe(map((r: any) => r as {
        status: 'queued' | 'processing' | 'done' | 'error' | 'none';
        error_message: string | null;
        version: number | null;
      }));
  }

  // ─── Lernziel-Register (PRD mastery-objectives M2/M3) ───────────────────

  getRegister(courseId: string) {
    return this.http
      .get(`${this.baseUrl}/course-structure/${courseId}/objectives`)
      .pipe(map((r: any) => r as ObjectiveRegister));
  }

  createObjective(courseId: string, body: {
    description: string; bloom_level?: string | null; dqr_area?: string | null;
    chapter_id?: string | null; source?: 'lecturer' | 'client';
  }) {
    return this.http.post<{ objective_key: string }>(`${this.baseUrl}/course-structure/${courseId}/objectives`, body);
  }

  updateObjective(courseId: string, key: string, body: Record<string, unknown>) {
    return this.http.patch<{ ok: boolean }>(
      `${this.baseUrl}/course-structure/${courseId}/objectives/${encodeURIComponent(key)}`, body);
  }

  importGoals(courseId: string, rows: { description: string; bloom_level?: string | null; external_ref?: string | null }[]) {
    return this.http.post<{ created: number; updated: number }>(
      `${this.baseUrl}/course-structure/${courseId}/goals/import`, { rows });
  }

  suggestGoalLinks(courseId: string) {
    return this.http.post<{ goals: number; proposed: number }>(
      `${this.baseUrl}/course-structure/${courseId}/goals/suggest-links`, {});
  }

  setGoalLink(courseId: string, targetKey: string, sourceKey: string, status: 'confirmed' | 'rejected' | 'proposed') {
    return this.http.put<{ ok: boolean }>(
      `${this.baseUrl}/course-structure/${courseId}/goals/${encodeURIComponent(targetKey)}/links/${encodeURIComponent(sourceKey)}`,
      { status });
  }

  getUnitScript(chapterId: string, unitNumber: number) {
    return this.http
      .get(`${this.baseUrl}/course-structure/unit-script/${chapterId}/${unitNumber}`)
      .pipe(map((r: any) => r as UnitScriptResponse));
  }

  /** File a correction request; queues a rework of that script. */
  addUnitScriptNote(chapterId: string, unitNumber: number, note: string) {
    return this.http
      .post(
        `${this.baseUrl}/course-structure/unit-script/${chapterId}/${unitNumber}/note`,
        { note },
      )
      .pipe(map((r: any) => r as { note_id: string; status: string }));
  }

  /** Latest parse-job state (queued | processing | done | error | none). */
  getParseStatusByCanonical(canonicalId: string) {
    return this.http
      .get(`${this.baseUrl}/course-structure/by-canonical/${canonicalId}/parse-status`)
      .pipe(map((r: any) => r as {
        status: 'queued' | 'processing' | 'done' | 'error' | 'none';
        error_message: string | null;
        version: number | null;
      }));
  }
}

export interface CourseDifficultyItem {
  objective_id: string;
  objective: string | null;
  chapter_id: string | null;
  chapter_title: string | null;
  chapter_order: number | null;
  fail_rate: number;
  attempts: number;
  sample_size: number;
}

export interface CourseDifficultyResponse {
  course_id: string | null;
  items: CourseDifficultyItem[];
}

export interface UnitScriptNote {
  id: string;
  note: string;
  status: 'open' | 'applied' | 'failed' | string;
  error_message?: string | null;
  created_at?: string;
  resolved_at?: string | null;
}

export interface UnitScriptResponse {
  script: {
    unit_title?: string;
    key_points?: string[];
    explanation_md?: string;
    worked_example?: string | null;
    misconceptions?: { claim: string; correction: string }[];
    check_questions?: any[];
    transition_in?: string;
    transition_out?: string;
    glossary?: { term: string; definition: string }[];
    figures?: { page: number; caption?: string }[];
  } | null;
  lang?: string | null;
  status?: string | null;
  updated_at?: string | null;
  notes: UnitScriptNote[];
}
